import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabaseClient'
import { registerSession } from '../lib/activeSession'
import AuthLayout from '../components/AuthLayout'
import Input from '../components/Input'
import { useLanguage } from '../hooks/useLanguage'

export default function Login() {
  const { t } = useLanguage()
  const navigate = useNavigate()
  const [form, setForm] = useState({ email: '', password: '' })
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [authError, setAuthError] = useState('')

  function handleChange(field) {
    return (e) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }))
      setErrors((prev) => ({ ...prev, [field]: '' }))
      setAuthError('')
    }
  }

  function validate() {
    const newErrors = {}
    if (!form.email) newErrors.email = t('login_error_email_required')
    else if (!/\S+@\S+\.\S+/.test(form.email)) newErrors.email = t('login_error_email_invalid')
    if (!form.password) newErrors.password = t('login_error_password_required')
    return newErrors
  }

  async function handleSubmit(e) {
    e.preventDefault()
    const newErrors = validate()
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return }

    setLoading(true)
    setAuthError('')

    const { data, error } = await supabase.auth.signInWithPassword({
      email: form.email,
      password: form.password,
    })

    if (error) {
      setLoading(false)
      if (error.message.includes('Invalid login credentials')) {
        setAuthError(t('login_error_invalid_credentials'))
      } else if (error.message.includes('Email not confirmed')) {
        setAuthError(t('login_error_email_not_confirmed'))
      } else {
        setAuthError(t('login_error_generic'))
      }
      return
    }

    // Registrar sesión activa
    await registerSession(data.user.id)

    setLoading(false)
    navigate('/home')
  }

  return (
    <AuthLayout>
      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 600, marginBottom: 6 }}>
        {t('login_title')}
      </h2>
      <p style={{ fontSize: 14, color: 'var(--color-muted)', marginBottom: 24 }}>
        {t('login_subtitle')}
      </p>

      {authError && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fecaca',
          borderRadius: 'var(--radius-sm)', padding: '10px 14px',
          marginBottom: 18, fontSize: 13, color: 'var(--color-danger)',
        }}>
          ⚠️ {authError}
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Input
          label={t('login_email_label')} type="email"
          value={form.email} onChange={handleChange('email')}
          placeholder={t('login_email_placeholder')} error={errors.email}
          required autoComplete="email"
        />
        <Input
          label={t('login_password_label')} type="password"
          value={form.password} onChange={handleChange('password')}
          placeholder={t('login_password_placeholder')} error={errors.password}
          required autoComplete="current-password"
        />

        <div style={{ textAlign: 'right', marginTop: -8 }}>
          <Link to="/forgot-password" style={{ fontSize: 13 }}>
            {t('login_forgot_password')}
          </Link>
        </div>

        <button
          type="submit" disabled={loading}
          style={{
            marginTop: 4, padding: '13px',
            background: loading ? '#86efac' : 'var(--color-primary)',
            color: 'var(--color-on-primary, #fff)', fontSize: 15, fontWeight: 600,
            borderRadius: 'var(--radius-sm)', boxShadow: 'var(--shadow-btn)',
            transition: 'background var(--transition)',
          }}
          onMouseEnter={(e) => { if (!loading) e.target.style.background = 'var(--color-primary-h)' }}
          onMouseLeave={(e) => { if (!loading) e.target.style.background = 'var(--color-primary)' }}
        >
          {loading ? t('login_loading') : t('login_submit')}
        </button>
      </form>

      <div style={{
        marginTop: 22, paddingTop: 20,
        borderTop: '1px solid var(--color-border)',
        textAlign: 'center', fontSize: 14, color: 'var(--color-muted)',
      }}>
        {t('login_no_account')}{' '}
        <Link to="/register" style={{ fontWeight: 600 }}>{t('login_register_link')}</Link>
      </div>
    </AuthLayout>
  )
}